import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookmarkSimple, Trash, Rocket, Planet } from "@phosphor-icons/react";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import api from "@/lib/api";

const TYPE_LABEL = {
  star: "Star", planet: "Planet", dwarf_planet: "Dwarf Planet", moon: "Moon",
  deep_space: "Deep Space", galaxy: "Galaxy", nebula: "Nebula",
  black_hole: "Black Hole", cluster: "Star Cluster",
};

export default function SavedViews() {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/saved-views").then((r) => setViews(r.data.saved_views)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    await api.delete(`/saved-views/${id}`);
    setViews((v) => v.filter((x) => x.id !== id));
    toast.success("Removed from collection");
  };

  return (
    <div className="min-h-screen" data-testid="saved-page">
      <TopNav />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 pt-28 pb-20">
        <div className="flex items-center gap-2 mb-4">
          <BookmarkSimple size={18} weight="duotone" color="#00D8FF" />
          <span className="text-tel text-[10px] text-cyan-400/80">Your Collection</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Saved Views</h1>
        <p className="text-white/50 mt-4">Objects you've bookmarked across the universe.</p>

        {loading ? (
          <div className="text-tel text-xs text-white/40 mt-16">Loading…</div>
        ) : views.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center mt-14">
            <Planet size={40} weight="duotone" color="#71717A" className="mx-auto" />
            <p className="text-white/50 mt-5">No saved views yet.</p>
            <button
              data-testid="saved-explore-cta"
              onClick={() => nav("/explore")}
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors"
            >
              <Rocket size={18} weight="fill" /> Start Exploring
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {views.map((v) => (
              <div key={v.id} data-testid={`saved-${v.object_id}`} className="glass rounded-2xl p-6 hover:border-cyan-400/30 transition-colors group">
                <div className="flex items-start justify-between">
                  <span className="w-5 h-5 rounded-full" style={{ background: v.color, boxShadow: `0 0 14px ${v.color}` }} />
                  <button data-testid={`delete-${v.object_id}`} onClick={() => remove(v.id)} className="text-white/30 hover:text-red-400 transition-colors">
                    <Trash size={18} weight="duotone" />
                  </button>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mt-4">{v.object_name}</h3>
                <div className="text-tel text-[9px] text-white/40 mt-1">{TYPE_LABEL[v.object_type] || v.object_type}</div>
                <button
                  data-testid={`open-${v.object_id}`}
                  onClick={() => nav(`/explore?object=${v.object_id}`)}
                  className="mt-5 text-sm text-white/70 group-hover:text-cyan-300 transition-colors"
                >
                  Open in explorer →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
